import { useEffect, useState } from "react";
import type { Entry, Layer } from "shared";
import { TextInput } from "@/components/forms/TextInput";
import { Button } from "@/components/primitives/Button";
import { FloatingCard } from "@/components/primitives/FloatingCard";
import { IconButton } from "@/components/primitives/IconButton";
import { IconX } from "@/components/primitives/icons";
import { MoveEntryDialog } from "./MoveEntryDialog";

type Props = {
	entry: Entry | null;
	currentMapId: string;
	layers: Layer[];
	saving?: boolean;
	onSave: (input: { title: string; description: string }) => Promise<void>;
	onMove: (target: { mapId: string; layerId: string; layerTitle: string }) => Promise<void>;
	onDelete: () => Promise<void>;
	onEditGeometry?: (entry: Entry) => void;
	onClose: () => void;
};

export function EntryEditor({
	entry,
	currentMapId,
	layers,
	saving,
	onSave,
	onMove,
	onDelete,
	onEditGeometry,
	onClose,
}: Props) {
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [moveOpen, setMoveOpen] = useState(false);

	useEffect(() => {
		if (!entry) {
			setTitle("");
			setDescription("");
			setError(null);
			setMoveOpen(false);
			return;
		}
		setTitle(entry.title);
		setDescription(entry.description ?? "");
		setError(null);
		setMoveOpen(false);
	}, [entry]);

	if (!entry) return null;

	const geomLabel =
		entry.geomType === "point"
			? "Punkt"
			: entry.geomType === "line"
				? "Linie"
				: "Bereich";

	const layerTitle =
		layers.find((l) => l.id === entry.layerId)?.title ?? "Ebene";

	return (
		<>
			<FloatingCard
				as="aside"
				className="flex max-h-full w-full flex-col overflow-y-auto p-3 max-md:rounded-b-none md:max-w-none"
			>
				<div className="mb-2 flex items-center justify-between gap-2">
					<p className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
						Detail · {geomLabel}
					</p>
					<IconButton label="Schließen" onClick={onClose}>
						<IconX />
					</IconButton>
				</div>
				<form
					className="flex flex-1 flex-col gap-3"
					onSubmit={(e) => {
						e.preventDefault();
						if (!title.trim()) {
							setError("Titel ist erforderlich");
							return;
						}
						setError(null);
						void onSave({ title: title.trim(), description: description.trim() });
					}}
				>
					<TextInput
						label="Titel"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						required
					/>
					<label className="flex flex-col gap-1.5 text-sm">
						<span className="text-[13px] font-medium text-fg">Beschreibung</span>
						<textarea
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
							className="rounded-[var(--radius-control)] bg-surface px-3 py-2 text-sm text-fg ring-1 ring-[var(--ring-hairline)] outline-none placeholder:text-fg-muted focus:ring-2 focus:ring-teal-500"
						/>
					</label>
					<p className="text-xs text-fg-muted">Ebene: {layerTitle}</p>
					{error ? (
						<p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>
					) : null}
					<div className="mt-auto flex flex-col gap-1.5">
						<Button type="submit" size="sm" disabled={saving}>
							{saving ? "Speichern…" : "Speichern"}
						</Button>
						{entry.geomType !== "point" && onEditGeometry ? (
							<Button
								type="button"
								size="sm"
								variant="secondary"
								disabled={saving}
								onClick={(e) => {
									e.currentTarget.blur();
									onEditGeometry(entry);
								}}
							>
								Form bearbeiten
							</Button>
						) : null}
						<Button
							type="button"
							size="sm"
							variant="secondary"
							disabled={saving}
							onClick={() => setMoveOpen(true)}
						>
							Verschieben…
						</Button>
						<Button
							type="button"
							size="sm"
							variant="danger"
							disabled={saving}
							onClick={() => void onDelete()}
						>
							Löschen
						</Button>
					</div>
				</form>
			</FloatingCard>
			<MoveEntryDialog
				open={moveOpen}
				entry={entry}
				currentMapId={currentMapId}
				currentLayers={layers}
				onClose={() => setMoveOpen(false)}
				onMove={onMove}
			/>
		</>
	);
}
